import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@vwfs-bronson/bronson-react';
import { Contract } from 'cpes-common';
import { InvoicesTab } from './paginated/InvoicesTab';
import { ContractDocumentationTab } from './paginated/ContractDocumentationTab';
import { ContractFilter } from './ui/filters/ContractFilter';
import { AllTimeframes, Timeframe } from './helper/Timeframe';

const invoicesTabId = 'invoices';
const documentsTabId = 'documents';

export const PostboxTabs: React.FC<{
    contracts: Contract[];
    initialContractNumber?: string;
    filterToInvoices?: boolean;
    initialTimeframe?: Timeframe | AllTimeframes;
}> = ({ contracts, initialContractNumber, filterToInvoices, initialTimeframe }) => {
    const { t } = useTranslation('postbox');
    const [selectedTab, setSelectedTab] = useState<string>(invoicesTabId);
    const [contractNumber, setContractNumber] = useState<string>(() =>
        contracts.some(contract => contract.contractNumber === initialContractNumber)
            ? (initialContractNumber as string)
            : 'ALL',
    );

    const hasLeasingOrRentingContract = contracts.some(
        contract => contract.isFinanceLeaseContract || contract.isOperatingLeaseContract,
    );
    const isDocumentsTabActive = !hasLeasingOrRentingContract || selectedTab === documentsTabId;

    const singleContractNumber = contracts.length === 1 ? contracts[0].contractNumber : undefined;
    const documentsContractNumber = contractNumber === 'ALL' ? singleContractNumber : contractNumber;

    const contractFilter = (
        <ContractFilter
            contracts={contracts}
            selectedContract={isDocumentsTabActive ? documentsContractNumber : contractNumber}
            allowAll={!isDocumentsTabActive}
            onChange={setContractNumber}
        />
    );

    if (!hasLeasingOrRentingContract) {
        return (
            <>
                {contractFilter}
                <ContractDocumentationTab contracts={contracts} contractNumber={documentsContractNumber} />
            </>
        );
    }

    return (
        <>
            {contractFilter}
            <Tabs selected={selectedTab} onChangeSelected={setSelectedTab}>
                <Tabs.Header>
                    <Tabs.HeaderItem tabId={invoicesTabId}>{t('tabs.invoices')}</Tabs.HeaderItem>
                    <Tabs.HeaderItem tabId={documentsTabId}>{t('tabs.documents')}</Tabs.HeaderItem>
                </Tabs.Header>
                <Tabs.Content>
                    <Tabs.ContentItem tabId={invoicesTabId}>
                        <InvoicesTab
                            contracts={contracts}
                            contractNumber={contractNumber}
                            initialTimeframe={filterToInvoices ? initialTimeframe : undefined}
                            isActive={selectedTab === invoicesTabId}
                        />
                    </Tabs.ContentItem>
                    <Tabs.ContentItem tabId={documentsTabId}>
                        <ContractDocumentationTab
                            contracts={contracts}
                            contractNumber={documentsContractNumber}
                            isActive={selectedTab === documentsTabId}
                        />
                    </Tabs.ContentItem>
                </Tabs.Content>
            </Tabs>
        </>
    );
};
